/**
 * Shopify Product API Service
 *
 * Uses Shopify's built-in .js endpoint to fetch product data
 * No API tokens or GraphQL needed!
 */

import { EnrichedShopifyProduct, ShopifyProduct } from '../types/shopify';
import { stubProduct } from '../constants/stub-data';
import { fetchVariantData, getMetafieldValue } from './shopify-storefront-api';
import { reportError } from '../config/newrelic';
import { response as productFetchCache } from '../data/product-fetch-cache';

/**
 * Fetch product data using Shopify's .js endpoint
 *
 * @param handle - The product handle (URL-friendly slug)
 * @returns Raw product data or null if not found
 *
 * @example
 * const product = await fetchProductByHandle('me3d-custom-insole');
 */
export async function fetchProductByHandle(handle: string): Promise<ShopifyProduct | null> {
  if (import.meta.env.VITE_STUB_DATA_FOR_DEVELOPMENT === 'true') {
    return stubProduct;
  }

  // --- 1. Fetch the product (isolated try-catch, falls back to cache on any failure) ---
  let product: ShopifyProduct;
  try {
    const response = await fetch(`/products/${handle}.json`);

    if (!response.ok) {
      const err = new Error(
        response.status === 404
          ? `Product not found: handle="${handle}"`
          : `Failed to fetch product: handle="${handle}" status=${response?.status} statusText="${response?.statusText}"`
      );
      console.error(err.message);
      reportError(err, { handle, context: 'fetchProductByHandle', status: response.status, endpoint: `/products/${handle}.json` });
      product = (productFetchCache as any).product as ShopifyProduct;
    } else {
      ({ product } = await response.json() as { product: ShopifyProduct });
    }
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    console.error(`Network error fetching product handle="${handle}", falling back to cache:`, err);
    reportError(err, { handle, context: 'fetchProductByHandle' });
    product = (productFetchCache as any).product as ShopifyProduct;
  }

  // --- 2. Fetch metafields and inventory availability (always runs; handles its own errors and cache fallback internally) ---
  const { metafieldsHash, availabilityHash } = await fetchVariantData(handle);

  if (Object.keys(metafieldsHash).length > 0 || Object.keys(availabilityHash).length > 0) {
    product.variants = product.variants.map(variant => {
      const metafields = metafieldsHash[variant.id];
      const available = availabilityHash[variant.id];
      return {
        ...variant,
        ...(metafields ? { metafields } : {}),
        ...(available !== undefined ? { available } : {}),
      };
    });
  }

  return product;
}

/**
 * Fetch product data and enrich it with pre-derived option lookups.
 *
 * @param handle - The product handle (URL-friendly slug)
 * @returns Enriched product data or null if not found
 */
export async function fetchEnrichedProductByHandle(handle: string): Promise<EnrichedShopifyProduct | null> {
  const product = await fetchProductByHandle(handle);
  if (!product) return null;

  const foamPackageOption = product.options.find(o => o.name === "Foam Package");
  const sizeOption = product.options.find(o => o.name === "Size");
  const foamPackages = foamPackageOption?.values.map(name => {
    const variant = product.variants.find(
      v => (v as unknown as Record<string, unknown>)[`option${foamPackageOption.position}`] === name
    );
    const imageSrc = getMetafieldValue(variant?.metafields, 'custom', 'material_image') || getVariantImageUrl(variant, product) || product?.images?.[0]?.src;
    const imageAlt = name;

    return {
      name,
      variant,
      price: variant?.price ?? "0",
      currency: variant?.price_currency ?? "USD",
      imageSrc,
      imageAlt,
    };
  }) ?? [];
  const sizeOptions = sizeOption?.values ?? [];

  return { ...product, foamPackageOption, sizeOption, foamPackages, sizeOptions };
}

/**
 * Helper to format price from string to currency string
 *
 * @param price - Price as string (e.g., "54.99") or number
 * @param currency - Currency code (e.g., "USD", "INR")
 * @returns Formatted price string (e.g., "$54.99")
 */
export function formatPrice(price: string | number, currency: string = 'USD'): string {
  const priceValue = typeof price === 'string' ? parseFloat(price) : price;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(priceValue);
}

/**
 * Calculate price difference between two prices
 *
 * @param variantPrice - Variant price as string (e.g., "54.99") or number
 * @param basePrice - Base price as string (e.g., "49.99") or number
 * @param currency - Currency code
 * @returns Formatted price difference string (e.g., "+$5.00" or "-$5.00")
 */
export function getPriceDifference(
  variantPrice: string | number,
  basePrice: string | number,
  currency: string = 'USD'
): string {
  const variantValue = typeof variantPrice === 'string' ? parseFloat(variantPrice) : variantPrice;
  const baseValue = typeof basePrice === 'string' ? parseFloat(basePrice) : basePrice;
  const diff = variantValue - baseValue;

  if (diff > 0) {
    return ` (+${formatPrice(diff, currency)})`;
  } else if (diff < 0) {
    return ` (${formatPrice(Math.abs(diff), currency)})`;
  }
  return '';
}

/**
 * Get the image URL for a variant, with fallback to product featured image
 *
 * @param variant - Product variant
 * @param product - Product data
 * @returns Image URL or empty string
 */
export function getVariantImageUrl(
  variant: { image_id: number | null },
  product: { images: { id: number; src: string }[]; image: { src: string } | null }
): string {
  // If variant has an image_id, find the matching image
  if (variant.image_id) {
    const variantImage = product.images.find(image => image.id === variant.image_id);
    if (variantImage) {
      return variantImage.src;
    }
  }

  // Fallback to product's main image or first image
  return product.image?.src || product.images?.[0]?.src || '';
}

